// Every mega menu reads the same pointer velocity, so track it once for the
// page rather than once per menu item. Refcounted: the listener only exists
// while at least one <mega-menu-opener> is connected.
const pointer = { velocity: 0 };
let lastPosition = null;
let trackerRefs = 0;

const trackPointer = (e) => {
  const position = { y: e.pageY, time: Date.now() };

  if (lastPosition) {
    const elapsed = position.time - lastPosition.time;
    // Two moves inside the same millisecond would divide by zero.
    if (elapsed > 0) {
      pointer.velocity = (position.y - lastPosition.y) / elapsed;
    }
  }

  lastPosition = position;
};

const retainPointerTracker = () => {
  if (trackerRefs++ === 0) {
    document.addEventListener("mousemove", trackPointer, { passive: true });
  }
};

const releasePointerTracker = () => {
  if (--trackerRefs > 0) return;

  trackerRefs = 0;
  lastPosition = null;
  document.removeEventListener("mousemove", trackPointer);
};

class MegaMenuOpener extends HTMLElement {
  closeTimeout = null;
  openTimeout = null;

  handleMouseover = () => {
    if (Math.abs(pointer.velocity) > 0.1) {
      if (this.openTimeout) return;

      this.openTimeout = setTimeout(() => {
        this._open();
      }, 100);
    } else {
      this._open();
    }
  };

  handleClick = (e) => {
    if (this.summary.contains(e.target) && !e.target.closest("a")) {
      e.preventDefault();
      this.details.open ? this._close() : this._open();
    }
  };

  handleMouseleave = () => {
    this._close();
  };

  handleFocusout = (e) => {
    if (this.content.contains(e.relatedTarget)) return;

    this._close();
  };

  closeEsc = (e) => {
    if (e.key === "Escape" && this.details.open) {
      this._close();
      this.summary.focus();
    }
  };

  connectedCallback() {
    this.details = this.querySelector("details");
    this.summary = this.querySelector("summary");
    this.content = this.querySelector("[data-mega-menu-content]");
    this.background = this.closest(".header")?.querySelector(
      "[data-mega-menu-background]",
    );

    if (!this.details || !this.summary || !this.content) return;

    retainPointerTracker();
    this.tracking = true;

    document.addEventListener("keydown", this.closeEsc);
    this.addEventListener("mouseover", this.handleMouseover);
    this.addEventListener("click", this.handleClick);
    this.addEventListener("mouseleave", this.handleMouseleave);
    this.content.addEventListener("focusout", this.handleFocusout);
  }

  disconnectedCallback() {
    if (this.tracking) {
      releasePointerTracker();
      this.tracking = false;
    }

    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);
    clearTimeout(this.settleTimeout);
    document.removeEventListener("keydown", this.closeEsc);
    this.removeEventListener("mouseover", this.handleMouseover);
    this.removeEventListener("click", this.handleClick);
    this.removeEventListener("mouseleave", this.handleMouseleave);
    this.content?.removeEventListener("focusout", this.handleFocusout);
  }

  _open() {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);
    this.openTimeout = null;
    this.removeEventListener("transitionend", this.onEnd);
    this.details.open = true;
    this.details.classList.add("open");
    document.body.classList.add("header-menu-open");

    if (this.background) {
      // Measured after `details.open`, so the panel has laid out. Moving between menus
      // rewrites this and the shared surface animates between the two heights.
      this.background.style.setProperty(
        "--active-height",
        `${this.content.getBoundingClientRect().height}px`,
      );
      this.background.classList.add("active");
    }

    // Panel contents wait for the surface to finish growing on a cold open, but appear
    // almost immediately once it is already open. `_mega-menu.scss` reads this class; the
    // delay tracks --transition-timing-x-small, which drives the surface's height.
    clearTimeout(this.settleTimeout);
    this.settleTimeout = setTimeout(() => {
      document.documentElement.classList.add("menu-background-open");
    }, 200);

    this.dispatchEvent(new CustomEvent("menu:open", { bubbles: true }));
  }

  _close(force) {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.openTimeout);
    clearTimeout(this.settleTimeout);
    this.removeEventListener("transitionend", this.onEnd);
    this.openTimeout = null;

    this.closeTimeout = setTimeout(
      () => {
        this.dispatchEvent(new CustomEvent("menu:close", { bubbles: true }));
        this.details.classList.remove("open");

        if (!document.querySelector(".header__main-nav-mega-menu.open")) {
          this.background?.classList.remove("active");
          document.documentElement.classList.remove("menu-background-open");
          document.body.classList.remove("header-menu-open");
        }

        this.onEnd = (e) => {
          if (e.target !== this.content) return;

          this.removeEventListener("transitionend", this.onEnd);

          if (this.details.classList.contains("open")) return;
          this.details.open = false;
        };
        this.addEventListener("transitionend", this.onEnd);
      },
      force ? 0 : 250,
    );
  }
}

if (!customElements.get("mega-menu-opener")) {
  customElements.define("mega-menu-opener", MegaMenuOpener);
}
